import { Feature, Position } from 'geojson';
import {
    PluginGeoLocation,
    PluginWorkSite,
} from 'rwl-shared/dist/models/plugin/Plugin';
import { isDefined } from '@realwork/crux-utils/array';

export const boundsFromWorkSites = (workSites: PluginWorkSite[]) => {
    const bounds = new google.maps.LatLngBounds();

    workSites.forEach((workSite) => {
        if (workSite.geolocation?.lat && workSite.geolocation?.lng) {
            bounds.extend(workSite.geolocation);
        }
    });

    return bounds;
};

export function boundsToString(bounds: google.maps.LatLngBounds) {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    return `${ne.lat()},${ne.lng()},${sw.lat()},${sw.lng()}`;
}

export function stringToBounds(url: string): google.maps.LatLngBounds {
    const [nelat, nelng, swlat, swlng] = url.split(',').map(Number.parseFloat);
    const ne = new google.maps.LatLng(nelat, nelng);
    const sw = new google.maps.LatLng(swlat, swlng);
    return new google.maps.LatLngBounds(sw, ne);
}

const createPolygon = (geometry: Position[]): google.maps.LatLngLiteral[] => {
    return geometry.map((c) => ({
        lng: c[0],
        lat: c[1],
    }));
};

export const createPathsFromGeoJSON = (
    geojson: Feature
): google.maps.LatLngLiteral[][] => {
    if (geojson.geometry.type === 'Polygon') {
        return geojson.geometry.coordinates.map((coords) =>
            createPolygon(coords as Position[])
        );
    } else if (geojson.geometry.type === 'MultiPolygon') {
        return geojson.geometry.coordinates.map((coords) =>
            createPolygon(coords[0] as Position[])
        );
    }

    return [];
};

function rad2degr(rad: number) {
    return (rad * 180) / Math.PI;
}
function degr2rad(degr: number) {
    return (degr * Math.PI) / 180;
}

function getLatLngCenter(
    latLngInDegr: { lat: number; lng: number }[]
): PluginGeoLocation {
    let sumX = 0;
    let sumY = 0;
    let sumZ = 0;
    let lat;
    let lng;
    for (let i = 0; i < latLngInDegr.length; i++) {
        const point = latLngInDegr[i];
        lat = degr2rad(point.lat);
        lng = degr2rad(point.lng);
        // sum of cartesian coordinates
        sumX += Math.cos(lat) * Math.cos(lng);
        sumY += Math.cos(lat) * Math.sin(lng);
        sumZ += Math.sin(lat);
    }

    const avgX = sumX / latLngInDegr.length;
    const avgY = sumY / latLngInDegr.length;
    const avgZ = sumZ / latLngInDegr.length;

    // convert average x, y, z coordinate to latitude and longitude
    lng = Math.atan2(avgY, avgX);
    const hyp = Math.sqrt(avgX * avgX + avgY * avgY);
    lat = Math.atan2(avgZ, hyp);

    return {
        latitude: rad2degr(lat),
        longitude: rad2degr(lng),
    };
}

export function getCenterPoint(workSites: PluginWorkSite[]): PluginGeoLocation {
    const locations = workSites
        .map((workSite) => workSite.geolocation)
        .filter(isDefined)
        .filter((location) => location.lat && location.lng);

    return getLatLngCenter(locations);
}
